import { useEffect, useCallback, useState, useRef } from "react";
import { useDocument } from "hooks/useDocument";
import { useFile } from "hooks/useFile";
import { Card, Text, Button, Table } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import tablestyles from "pages/dashboard/tablestyles.module.css";
import ImageIcon from "@rsuite/icons/Image";
import CollaspedOutlineIcon from "@rsuite/icons/CollaspedOutline";
import ExpandOutlineIcon from "@rsuite/icons/ExpandOutline";
import FileDownloadIcon from "@rsuite/icons/FileDownload";
//import { locale } from "moment/moment";

const { Column, HeaderCell, Cell } = Table;

function TablePanel({ title, collectionName, backgroundColor }) {
  const { retrieveDocs } = useDocument();
  const [refreshCounter, setRefreshCounter] = useState(0); // State to trigger re-render
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [tableData, setTableData] = useState([]);
  const runOnceRef = useRef(true);
  const actuationCount = useRef(0);
  const numSupernatant = useRef(0);
  const numSludge = useRef(0);
  const drainingDuration = useRef(0);

  const { getFileRef } = useFile();

  /* [2] Table */
  const [sortColumn, setSortColumn] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);

  // 2.1 Sample data for the table
  // 2.2 Function to sort the table data based on the current sort column and type
  const getData = () => {
    if (sortColumn && sortType) {
      return tableData.sort((a, b) => {
        let x = a[sortColumn];
        let y = b[sortColumn];
        if (typeof x === "string") {
          x = x.charCodeAt();
        }
        if (typeof y === "string") {
          y = y.charCodeAt();
        }
        if (sortType === "asc") {
          return x - y;
        } else {
          return y - x;
        }
      });
    }
    return tableData;
  };

  const handleSortColumn = (sortColumn, sortType) => {
    setTableLoading(true);
    setTimeout(() => {
      setTableLoading(false);
      setSortColumn(sortColumn);
      setSortType(sortType);
    }, 500);
  };

  const validateOperation = useCallback(async () => {
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-based
      const day = String(today.getDate()).padStart(2, "0");

      // Format as yyyy-mm-dd
      const localeDate = `${year}-${month}-${day}`;

      // comb
      const combQueries = {
        whereQueries: [
          {
            field: "date",
            condition: "==",
            value: localeDate,
          },
          {
            field: "unixts",
            condition: "!=",
            value: null,
          },
        ],
        orderByQueries: [
          {
            field: "unixts",
            direction: "desc",
          },
        ],
      };

      const retrievedDocs = await retrieveDocs(collectionName, combQueries);

      const tableData = retrievedDocs.map((doc, index) => ({
        id: index + 1,
        datetime: doc.data.date + " " + doc.data.time,
        ...doc.data,
      }));

      tableData.forEach((item) => {
        if (parseFloat(item.relayOpenDuration_min) < 0) {
          // Handle bug: 23:50:00 - 00:00:00 is -1430 min
          const duration = (
            24 * 60 +
            parseFloat(item.relayOpenDuration_min)
          ).toFixed(1);
          item.relayOpenDuration_min = String(duration); // relayOpenDuration_min is stored in String format
        }
      });
      actuationCount.current = tableData.length;

      const detectionResults = tableData.map((obj) => obj.result);
      numSupernatant.current = detectionResults.filter(
        (item) => item === "supernatant",
      ).length;
      numSludge.current = detectionResults.filter(
        (item) => item === "sludge",
      ).length;

      const durations = tableData.reduce(
        (sum, obj) => sum + parseFloat(obj.relayOpenDuration_min),
        0,
      );
      drainingDuration.current = durations;

      setTableData(tableData);

      setRefreshCounter(refreshCounter + 1); // Trigger re-render
    } catch (err) {
      console.log(err);
    }
  }, [retrieveDocs, refreshCounter, collectionName]);

  useEffect(() => {
    if (runOnceRef.current) {
      validateOperation();
      runOnceRef.current = false; // Set to false after the first run
    }

    const intervalId = setInterval(() => {
      validateOperation();
    }, 10000); // Run every 10 seconds

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [validateOperation]);

  const downloadCSV = () => {
    const header = [
      "Id",
      "Date Time",
      "Actuator Open",
      "Actuator Close",
      "Duration (min)",
      "Detection",
    ];
    const rows = tableData.map((row) => [
      row.id,
      row.datetime,
      row.relayOpenTS,
      row.relayCloseTS,
      row.relayOpenDuration_min,
      row.result,
    ]);
    let csvContent =
      "data:text/csv;charset=utf-8," +
      header.join(",") +
      "\n" +
      rows.map((e) => e.join(",")).join("\n");

    const getFormattedNow = () => {
      const now = new Date();

      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are 0-based
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");

      return `${year}${month}${day}${hours}${minutes}${seconds}`;
    };

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    const filename = `SSTD${title.slice(-1)}_${getFormattedNow()}.csv`;
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link); // Required for FF
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card size="md" shaded>
      <Card.Header
        backgroundColor={backgroundColor}
        style={{ paddingLeft: "20px", paddingBottom: "10px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Text
            size="sm"
            style={{
              textAlign: "left",
              width: "100%",
              color: "white",
            }}
          >
            {title}
          </Text>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: "0px",
            }}
          >
            <Button
              appearance="appearance"
              style={{ backgroundColor: backgroundColor, color: "white" }}
              //style={{ color: { backgroundColor } }}
              onClick={() => {
                downloadCSV();
              }}
            >
              <FileDownloadIcon />
            </Button>
            <Button
              appearance="appearance"
              style={{ backgroundColor: backgroundColor, color: "white" }}
              //style={{ color: { backgroundColor } }}
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
            >
              {isCollapsed ? <ExpandOutlineIcon /> : <CollaspedOutlineIcon />}
            </Button>
          </div>
        </div>
        <Text
          size="md"
          style={{
            textAlign: "left",
            width: "100%",
            color: "white",
          }}
        >
          Actuation Count: {actuationCount.current}
        </Text>
        <Text
          size="md"
          style={{
            textAlign: "left",
            width: "100%",
            color: "white",
          }}
        >
          No. Supernatant: {numSupernatant.current}
        </Text>
        <Text
          size="md"
          style={{
            textAlign: "left",
            width: "100%",
            color: "white",
          }}
        >
          No. Sludge: {numSludge.current}
        </Text>
        <Text
          size="md"
          style={{
            textAlign: "left",
            width: "100%",
            color: "white",
          }}
        >
          Draining Duration: {drainingDuration.current.toFixed(1)} (min)
        </Text>
      </Card.Header>
      <Card.Body>
        {isCollapsed ? (
          <Text size="sm" style={{ textAlign: "center", width: "100%" }}>
            Click the button{" "}
            <Button
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
            >
              <ExpandOutlineIcon />
            </Button>{" "}
            to expand the table
          </Text>
        ) : (
          <Table
            height={520}
            data={getData()}
            sortColumn={sortColumn}
            sortType={sortType}
            onSortColumn={handleSortColumn}
            loading={tableLoading}
            bordered={true}
            locale={{
              emptyMessage: "",
            }}
          >
            {/* Id */}
            <Column width={60} align="center" sortable>
              <HeaderCell className={tablestyles.customHeader}>Id</HeaderCell>
              <Cell dataKey="id" className={tablestyles.customCell} />
            </Column>

            {/* Date time */}
            <Column flexGrow={1} align="center">
              <HeaderCell className={tablestyles.customHeader}>
                Date Time
              </HeaderCell>
              <Cell dataKey="datetime" className={tablestyles.customCell} />
            </Column>

            {/* Actuator Open */}
            <Column flexGrow={1} align="center">
              <HeaderCell className={tablestyles.customHeader}>
                Actuator Open
              </HeaderCell>
              <Cell dataKey="relayOpenTS" className={tablestyles.customCell} />
            </Column>

            {/* Actuator Close */}
            <Column flexGrow={1} align="center">
              <HeaderCell className={tablestyles.customHeader}>
                Actuator Close
              </HeaderCell>
              <Cell dataKey="relayCloseTS" className={tablestyles.customCell} />
            </Column>

            {/* Duration */}
            <Column flexGrow={1} align="center">
              <HeaderCell className={tablestyles.customHeader}>
                Duration (min)
              </HeaderCell>
              <Cell
                dataKey="relayOpenDuration_min"
                className={tablestyles.customCell}
              />
            </Column>

            {/* Detection */}
            <Column flexGrow={1} align="center">
              <HeaderCell className={tablestyles.customHeader}>
                Detection
              </HeaderCell>
              <Cell dataKey="result" className={tablestyles.customCell} />
            </Column>

            {/* Edit Button */}
            <Column width={100} align="center">
              <HeaderCell>...</HeaderCell>
              <Cell style={{ padding: "3px" }}>
                {(rowData) => (
                  <Button
                    appearance="primary"
                    onClick={async () => {
                      const fileRef = await getFileRef(rowData.path);
                      const url = fileRef.url ? fileRef.url : null;
                      window.open(url, "_blank");
                      /*console.log(
                      "Edit clicked for row:",
                      JSON.stringify(rowData, null, 2),
                    );*/
                    }}
                  >
                    <ImageIcon />
                  </Button>
                )}
              </Cell>
            </Column>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

export default TablePanel;
